import type { CostRow } from '../types'
import type { ColumnId } from './columns'
import { editableColumns, isCellEditable, type BudgetAffordances, type EditableColumn, type EditingCell } from './permissions'

export type CellMove = 'down' | 'next' | 'prev' | 'stay'
export type CommitValue =
  | { column: 'name' | 'code'; text: string }
  | { column: 'originalBudget' | 'estimate' | 'projectedFinalCost'; money: string | null }

interface RowLike { node: { activity: CostRow } }

/**
 * Where the editor goes after a commit: Enter → the same column on the next row below that can edit it;
 * Tab / Shift+Tab → the next / previous editable cell, wrapping across rows; blur → nowhere.
 */
export function nextEditingCell(rows: RowLike[], nodeId: string, column: EditableColumn, move: CellMove, af: BudgetAffordances, visible: ReadonlySet<ColumnId>): EditingCell | null {
  if (move === 'stay') return null
  const at = rows.findIndex((r) => r.node.activity.id === nodeId)
  if (at < 0) return null
  if (move === 'down') {
    for (let i = at + 1; i < rows.length; i++) {
      const next = rows[i].node.activity
      if (isCellEditable(next, column, af)) return { nodeId: next.id, column }
    }
    return null
  }
  const dir = move === 'next' ? 1 : -1
  let i = at
  let cols = editableColumns(rows[i].node.activity, af, visible)
  let c = cols.indexOf(column)
  for (;;) {
    c += dir
    if (c >= 0 && c < cols.length) return { nodeId: rows[i].node.activity.id, column: cols[c] }
    i += dir
    if (i < 0 || i >= rows.length) return null
    cols = editableColumns(rows[i].node.activity, af, visible)
    c = dir === 1 ? -1 : cols.length
  }
}

/**
 * The updateCostNode input for an edited cell, or null when nothing changed. Typing the computed forecast back
 * does not pin it; an emptied forecast clears a manual one; an emptied original budget is zero.
 */
export function commitInput(row: CostRow, value: CommitValue): Record<string, unknown> | null {
  switch (value.column) {
    case 'name':
      return value.text && value.text !== row.name ? { name: value.text } : null
    case 'code':
      return value.text !== (row.code ?? '') ? { code: value.text } : null
    case 'originalBudget': {
      const next = value.money ?? '0.00'
      return next !== row.values.originalBudget ? { originalBudget: next } : null
    }
    case 'estimate':
      if (value.money === null) return row.estimate !== null ? { clearEstimate: true } : null
      return value.money !== row.estimate ? { estimate: value.money } : null
    case 'projectedFinalCost':
      if (value.money === null) return row.hasForecastOverride ? { clearForecastOverride: true } : null
      return value.money !== row.values.projectedFinalCost ? { forecastOverride: value.money } : null
    default:
      return null
  }
}
